/**
 * JourneySignaturePad — a DocuSign-style e-signature capture. The signer chooses Draw (canvas) or
 * Type (their name rendered in a chosen handwriting font); either way it produces a PNG. Calls
 * onChange({ name, dataUrl, method, font }) on every change — dataUrl is '' until a signature exists.
 * Used by the in-journey ASR and proposal sign flows.
 */
import { useEffect, useRef, useState } from 'react'

const FONTS = ['Dancing Script', 'Great Vibes', 'Sacramento', 'Homemade Apple']
const FONT_QUERY = 'Dancing+Script:wght@600|Great+Vibes|Sacramento|Homemade+Apple'
let fontsRequested = false
function ensureFonts() {
  if (fontsRequested || typeof document === 'undefined') return
  fontsRequested = true
  const l = document.createElement('link')
  l.rel = 'stylesheet'
  l.href = 'https://fonts.googleapis.com/css?family=' + FONT_QUERY + '&display=swap'
  document.head.appendChild(l)
}

export default function JourneySignaturePad({ onChange }) {
  const canvasRef = useRef(null)
  const drawing = useRef(false)
  const inked = useRef(false)
  const [mode, setMode] = useState('draw')
  const [name, setName] = useState('')
  const nameRef = useRef('')          // always-current copy for stale-closure-safe handlers
  const [font, setFont] = useState(FONTS[0])

  useEffect(() => { ensureFonts() }, [])

  function renderTyped(text, fontFamily) {
    const c = document.createElement('canvas'); c.width = 560; c.height = 150
    const cx = c.getContext('2d')
    cx.clearRect(0, 0, c.width, c.height)
    cx.fillStyle = '#07272D'; cx.textBaseline = 'middle'; cx.textAlign = 'center'
    let size = 70; cx.font = `${size}px "${fontFamily}", cursive`
    while (cx.measureText(text).width > c.width - 40 && size > 22) { size -= 4; cx.font = `${size}px "${fontFamily}", cursive` }
    cx.fillText(text, c.width / 2, c.height / 2 + 4)
    return c.toDataURL('image/png')
  }

  // Emit the current signature state. For typed mode, wait for the chosen font to load so the PNG
  // is rendered in the right hand (next overrides cover not-yet-committed state on the same tick).
  function emit(next = {}) {
    const st = { mode, name, font, ...next }
    const nm = st.name.trim()
    const fire = (dataUrl, method, fnt) => { if (onChange) onChange({ name: nm, dataUrl, method, font: fnt }) }
    if (st.mode === 'type') {
      const done = () => fire(nm ? renderTyped(nm, st.font) : '', 'type', st.font)
      if (nm && typeof document !== 'undefined' && document.fonts && document.fonts.load) document.fonts.load(`70px "${st.font}"`).then(done).catch(done)
      else done()
    } else {
      const c = canvasRef.current
      fire(inked.current && c ? c.toDataURL('image/png') : '', 'draw', null)
    }
  }

  // Draw-mode canvas wiring (re-attached when entering draw mode).
  useEffect(() => {
    if (mode !== 'draw') return
    const c = canvasRef.current; if (!c) return
    const cx = c.getContext('2d')
    cx.lineWidth = 2.4; cx.lineCap = 'round'; cx.lineJoin = 'round'; cx.strokeStyle = '#07272D'
    const pos = e => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) } }
    const down = e => { drawing.current = true; const p = pos(e); cx.beginPath(); cx.moveTo(p.x, p.y); e.preventDefault() }
    const move = e => { if (!drawing.current) return; const p = pos(e); cx.lineTo(p.x, p.y); cx.stroke(); inked.current = true; e.preventDefault() }
    // Read nameRef (not name) here — this closure is frozen at effect-setup time and `name` would
    // be stale if the user typed their name after entering draw mode.
    const up = () => {
      if (!drawing.current) return
      drawing.current = false
      const dataUrl = inked.current && c ? c.toDataURL('image/png') : ''
      if (onChange) onChange({ name: nameRef.current.trim(), dataUrl, method: 'draw', font: null })
    }
    c.addEventListener('pointerdown', down); c.addEventListener('pointermove', move); window.addEventListener('pointerup', up)
    return () => { c.removeEventListener('pointerdown', down); c.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  function clearDraw() { const c = canvasRef.current; if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height); inked.current = false; emit() }
  function pickMode(m) { if (m === mode) return; setMode(m); setTimeout(() => emit({ mode: m }), 0) }
  function setNm(v) { setName(v); nameRef.current = v; emit({ name: v }) }
  function pickFont(fnt) { setFont(fnt); emit({ font: fnt }) }

  return (
    <div>
      <div style={S.tabs}>
        <button type="button" style={{ ...S.tab, ...(mode === 'draw' ? S.tabOn : {}) }} onClick={() => pickMode('draw')}>Draw</button>
        <button type="button" style={{ ...S.tab, ...(mode === 'type' ? S.tabOn : {}) }} onClick={() => pickMode('type')}>Type</button>
      </div>

      <input style={S.name} placeholder="Type your full name" value={name} onChange={e => setNm(e.target.value)} />

      {mode === 'draw' ? (
        <div style={S.padWrap}>
          <canvas ref={canvasRef} width={560} height={150} style={S.canvas} />
          <button type="button" style={S.clear} onClick={clearDraw}>Clear</button>
        </div>
      ) : (
        <div>
          <div style={S.fontRow}>
            {FONTS.map(fnt => {
              const chipSize = Math.max(11, 22 - Math.max(0, (name.trim().length || 0) - 8) * 1.2)
              return (
                <button key={fnt} type="button"
                  style={{ ...S.fontChip, fontFamily: `"${fnt}", cursive`, fontSize: chipSize, ...(font === fnt ? S.fontChipOn : {}) }}
                  onClick={() => pickFont(fnt)}>
                  {name.trim() || 'Signature'}
                </button>
              )
            })}
          </div>
          <div style={S.typePreview}>
            <span style={{ fontFamily: `"${font}", cursive`, fontSize: Math.max(18, 40 - Math.max(0, (name.trim().length || 0) - 8) * 1.5), color: '#07272D', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}>{name.trim() || 'Your signature'}</span>
          </div>
        </div>
      )}
      <div style={S.hint}>{mode === 'draw' ? 'Draw your signature above.' : 'Pick a style for your signature.'}</div>
    </div>
  )
}

const S = {
  tabs: { display: 'flex', gap: 6, marginBottom: 10 },
  tab: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: '#5a7884', background: 'rgba(7,39,45,0.06)', border: 'none', borderRadius: 20, padding: '5px 16px', cursor: 'pointer' },
  tabOn: { color: '#fff', background: '#07272D' },
  name: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: '#07272D', padding: '10px 12px', border: '1px solid rgba(7,39,45,0.3)', borderRadius: 6, background: '#fff', boxSizing: 'border-box', marginBottom: 8 },
  padWrap: { position: 'relative', border: '1px solid rgba(7,39,45,0.2)', borderRadius: 6, background: '#fff', overflow: 'hidden' },
  canvas: { width: '100%', height: 150, display: 'block', touchAction: 'none', cursor: 'crosshair' },
  clear: { position: 'absolute', top: 6, right: 8, fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#1a6fa8', background: 'rgba(255,255,255,0.8)', border: 'none', cursor: 'pointer' },
  fontRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 8 },
  fontChip: { display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#07272D', background: '#fff', border: '0.5px solid rgba(7,39,45,0.2)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', height: 44 },
  fontChipOn: { borderColor: '#1D9E75', boxShadow: '0 0 0 2px rgba(29,158,117,0.25)' },
  typePreview: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 90, border: '0.5px solid rgba(7,39,45,0.25)', borderRadius: 6, background: '#fff' },
  hint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'rgba(7,39,45,0.45)', marginTop: 6 },
}
